import { useToastr } from '@farmshare/ui-components';
import {
  animalSpeciesHelper,
  animalSplitTypeHelper,
  calculateSplitHangingWeight,
} from '@farmshare/utils';
import {
  alpha,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Fragment, useMemo, useRef, useState } from 'react';

import { useUserState } from 'providers/user-state-provider';
import { ModuleId } from 'lib/modules/module-id.enum';
import { downloadFromBase64Encoded } from 'lib/downloadFromBase64Encoded';
import { downloadFromPresignedUrl } from 'lib/downloadFromPresignedUrl';

import {
  type Cutsheet,
  EnumCutsheetAnimalSpecies,
  EnumCutsheetInspectionLevels,
  EnumProcessorSchedulingStatus,
  useCutsheetCapabilityOptionsByIdsQuery,
  useCutsheetQuestionnairesByIdsQuery,
  useProcessorSchedulingGeneratePackingSlipMutation,
  EnumUserRelationshipsType,
} from 'lib/graphql';

import { isBuyerOnly, isProcessor } from 'lib/userHelpers';

import { EditYieldModal } from 'pages/processing-job/generate-invoice/edit-yield-modal';
import {
  buildV1YieldSources,
  buildV2YieldSources,
} from 'pages/processing-job/generate-invoice/priced-items';
import type {
  YieldItem,
  YieldSource,
} from 'pages/processing-job/generate-invoice/types';

import { useHeadSplitSection } from './use-head-split-section';

import { ExistingBuyerDrawer } from './existing-buyer-drawer';
import { ConfirmDestructiveDialog } from '../../../shared/confirm-destructive-dialog';
import { AnimalHead, Job } from '../../../shared/split-slot/types';
import {
  chunkSlots,
  getSplitDescription,
} from '../../../shared/split-slot/utils';
import { CutsheetWizardForm } from 'features/capabilities/cutsheets/wizard/form/cutsheet-wizard-form';

import { useWizardData } from '../../../shared/hooks/use-wizard-data';
import { NewBuyerDrawer } from './new-buyer-drawer';
import { ProducerCutsheetDrawer } from '../../../shared/cutsheet-drawer';
import { ProducerQuestionnaireEditWizard } from '../../../shared/questionnaire-edit-wizard';
import { ProducerQuestionnaireResponseDrawer } from '../../../shared/questionnaire-response-drawer';
import { BuyerSplitSlot } from '../../../shared/split-slot/buyer-split-slot';
import { SplitSlot } from '../../../shared/split-slot/split-slot';
import { CutsheetResponsePreview } from 'features/customers/components/_tabs/cutsheet-response-preview';

interface HeadSplitSectionProps {
  head: AnimalHead;
  index: number;
  job: Job;
  readOnly?: boolean;
}

export function HeadSplitSection({
  head,
  index,
  job,
  readOnly = false,
}: HeadSplitSectionProps) {
  const speciesLabel = animalSpeciesHelper(job.animalSpecies).label;

  const totalHangingWeight = head?.isHangingWeightPerSide
    ? (head?.hangingWeightSideA ?? 0) + (head?.hangingWeightSideB ?? 0)
    : (head?.hangingWeight ?? 0);

  const producerId = head?.producerIdentifier?.trim();

  const { isActiveModule } = useUserState();
  const hasCapabilitiesV2 = isActiveModule(ModuleId.CAPABILITIES_V2);

  const {
    user,
    vendor,
    selectedSplit,
    availableSplitTypes,
    slots,
    slotContacts: slotBuyerInfos,
    savingSplitType,
    generatingPdf,
    drawerSlotIndex,
    setDrawerSlotIndex,
    newBuyerSlotIndex,
    setNewBuyerSlotIndex,
    cutsheetDrawerSlotIndex,
    setCutsheetDrawerSlotIndex,
    cutsheetDrawerInfo,
    wizardSlotIndex,
    setWizardSlotIndex,
    handleWizardCutsheetCreated,
    handleSelectSplitType,
    handleSelectMyself,
    handleLeaveUnassigned,
    handleSelectExisting,
    handleNewBuyerCreated,
    handleDownloadPdf,
    handleSendReminder,
    sendingReminder,
    slotCanSendReminder,
    getReminderSentTo,
    questionnaireOptions,
    getSlotQuestionnaireId,
    handleSelectQuestionnaire,
    questionnaireResponseDrawerSlotIndex,
    setQuestionnaireResponseDrawerSlotIndex,
    questionnaireResponseDrawerInfo,
    questionnaireEditWizardSlotIndex,
    setQuestionnaireEditWizardSlotIndex,
    questionnaireEditWizardInfo,
    handleToggleSlotLock,
    togglingLockSlotId,
  } = useHeadSplitSection(job, head, hasCapabilitiesV2);

  const buyerMode = user ? isBuyerOnly(user) : false;
  const processorMode = user ? isProcessor(user) : false;
  const myselfLabel = processorMode ? 'Producer' : 'Myself';
  // For coloring, the processor isn't a buyer on any slot, so treat the
  // producer as the "self" identity when the processor is viewing.
  const selfUserId = processorMode ? job.requestedBy?._id : user?._id;
  const [previewResponseId, setPreviewResponseId] = useState<string | null>(
    null,
  );

  // For buyers, only show slots where they are the assigned buyer
  const visibleSlotIndices = useMemo(() => {
    if (!buyerMode) return slots.map((_s, i) => i);
    return slots.reduce<number[]>((acc, _s, i) => {
      if (slotBuyerInfos[i]?.contactUser?._id === user?._id) acc.push(i);
      return acc;
    }, []);
  }, [buyerMode, slots, slotBuyerInfos, user?._id]);

  const {
    wizardDataReady,
    wizardCapabilities,
    wizardSettings,
    templateCutsheetRef,
    fetchWizardDataAndSetReady: fetchWizardData,
  } = useWizardData();

  // Capture wizard context at open time so it's stable across re-renders.
  // Without this, slotBuyerInfos recomputation (from query refetches) can
  // momentarily null out the buyer, unmounting the wizard mid-flow.
  const wizardContextRef = useRef<{
    cutsheetType: 'producer' | 'buyer';
    buyerId?: string;
    producerId?: string;
    userId?: string;
  } | null>(null);

  const theme = useTheme();
  const isPendingAcceptance =
    job.status === EnumProcessorSchedulingStatus.Requested;
  const hasLockedSlot = slotBuyerInfos.some((b) => b?.locked === true);

  const [pendingSplitType, setPendingSplitType] = useState<string | null>(null);

  // Per-slot yield modal + packing-slip download (processor + producer surfaces)
  const [editYieldSlotIndex, setEditYieldSlotIndex] = useState<number | null>(
    null,
  );
  const [packingSlipSlotIndex, setPackingSlipSlotIndex] = useState<
    number | null
  >(null);
  const [generatePackingSlip] =
    useProcessorSchedulingGeneratePackingSlipMutation();
  const { push: pushToastr } = useToastr();

  // For V2 yield sourcing: gather every option/questionnaire id referenced by
  // a V2 questionnaire response on this head, fetch them, then build maps the
  // EditYieldModal can use.
  const { v2QuestionnaireIds, v2OptionIds } = useMemo(() => {
    const qids = new Set<string>();
    const oids = new Set<string>();
    for (const info of slotBuyerInfos) {
      const r = info?.questionnaireResponse;
      if (!r) continue;
      if (r.questionnaire) qids.add(r.questionnaire.toString());
      for (const c of r.selectedCuts ?? []) {
        if (c?.optionId) oids.add(c.optionId);
      }
      for (const t of r.trimSelections?.trim ?? []) {
        if (t?.optionId) oids.add(t.optionId);
      }
      for (const e of r.extraSelections?.extras ?? []) {
        if (e?.optionId) oids.add(e.optionId);
      }
    }
    return {
      v2QuestionnaireIds: Array.from(qids),
      v2OptionIds: Array.from(oids),
    };
  }, [slotBuyerInfos]);

  const { data: v2QuestionnairesData } = useCutsheetQuestionnairesByIdsQuery({
    variables: { ids: v2QuestionnaireIds },
    skip: v2QuestionnaireIds.length === 0,
  });
  const { data: v2OptionsData } = useCutsheetCapabilityOptionsByIdsQuery({
    variables: { ids: v2OptionIds },
    skip: v2OptionIds.length === 0,
  });

  const v2QuestionnairesById = useMemo(() => {
    const map = new Map<
      string,
      NonNullable<
        NonNullable<typeof v2QuestionnairesData>['cutsheetQuestionnaireByIds']
      >[number]
    >();
    for (const q of v2QuestionnairesData?.cutsheetQuestionnaireByIds ?? []) {
      if (q?._id) map.set(q._id.toString(), q);
    }
    return map;
  }, [v2QuestionnairesData]);

  const v2OptionsById = useMemo(() => {
    const map = new Map<
      string,
      NonNullable<
        NonNullable<typeof v2OptionsData>['cutsheetCapabilityOptionByIds']
      >[number]
    >();
    for (const o of v2OptionsData?.cutsheetCapabilityOptionByIds ?? []) {
      if (o?._id) map.set(o._id.toString(), o);
    }
    return map;
  }, [v2OptionsData]);

  async function handleGeneratePackingSlip(slotIdx: number) {
    const info = slotBuyerInfos[slotIdx];
    if (!info?._id) return;
    setPackingSlipSlotIndex(slotIdx);
    try {
      const result = await generatePackingSlip({
        variables: {
          processorSchedulingId: job._id,
          cutsheetInformationId: info._id,
        },
      });
      const resp = result.data?.processorSchedulingGeneratePackingSlip;
      if (resp?.errors && resp.errors.length > 0) {
        throw new Error(resp.errors.join(', '));
      }
      if (resp?.fileUrl) {
        downloadFromPresignedUrl(resp.fileUrl);
      } else if (resp?.file && resp?.filename) {
        downloadFromBase64Encoded(resp.file, resp.filename);
      } else {
        throw new Error('Packing slip not generated.');
      }
    } catch (error) {
      pushToastr({
        title: 'Packing slip',
        body: (error as Error).message,
        bg: 'danger',
        delay: 5000,
      });
    } finally {
      setPackingSlipSlotIndex(null);
    }
  }

  // Build a narrow split object the EditYieldModal can consume.
  function buildEditYieldSplit(slotIdx: number) {
    const info = slotBuyerInfos[slotIdx];
    if (!info?._id) return null;

    const isV2 = !info.cutsheet && !!info.questionnaireResponse;

    let yieldSources: YieldSource[] = [];
    if (isV2 && info.questionnaireResponse) {
      const questionnaire = info.questionnaireResponse.questionnaire
        ? v2QuestionnairesById.get(
            info.questionnaireResponse.questionnaire.toString(),
          )
        : null;
      yieldSources = buildV2YieldSources({
        response: info.questionnaireResponse,
        optionsById: v2OptionsById,
        questionnaire,
      });
    } else if (info.cutsheet) {
      yieldSources = buildV1YieldSources({
        primalCuts: info.cutsheet.primalCuts,
        extras: info.cutsheet.extras,
        trim: info.cutsheet.trim,
      });
    }

    const yieldItems: YieldItem[] =
      info.yield?.map((y) => ({
        capabilityOptionId: y?.capabilityOptionId ?? undefined,
        label: y?.label ?? '',
        weightLbs: y?.weightLbs ?? 0,
        itemCount: y?.itemCount ?? 0,
        packageCount: y?.packageCount ?? 0,
        packageIdentifier: y?.packageIdentifier ?? undefined,
      })) ?? [];

    return {
      id: `${head?.publicId ?? ''}-${slotIdx}`,
      cutsheetInformationId: info._id,
      animalHeadPublicId: head?.publicId ?? '',
      tracking: head?.tracking,
      splitPart: info.splitPart || 'whole',
      yieldSources,
      yieldItems,
    };
  }

  const hasAnyAssignedSlot = slotBuyerInfos.some((b) => !!b?.contactUser?._id);

  const requestSelectSplitType = (opt: string) => {
    if (opt === selectedSplit) return;
    if (hasAnyAssignedSlot) {
      setPendingSplitType(opt);
      return;
    }
    handleSelectSplitType(opt);
  };

  /**
   * Determines the health color of the selected split type:
   *  - 'error'   — any slot missing buyer or cutsheet (needs producer action)
   *  - 'warning' — all buyers assigned, but some non-self slots awaiting buyer cutsheet
   *  - 'success' — all slots have buyer + cutsheet
   */
  type SplitHealth = 'error' | 'warning' | 'success';
  const splitHealth: SplitHealth = useMemo(() => {
    if (!selectedSplit || slots.length === 0) return 'error';

    let allComplete = true;
    let hasWarning = false;

    for (let i = 0; i < slots.length; i++) {
      const buyer = slotBuyerInfos[i];

      // No buyer assigned → red
      if (!buyer?.contactUser) return 'error';

      // Locked by processor → warning (producer can't change it)
      if (buyer.locked) {
        allComplete = false;
        hasWarning = true;
        continue;
      }

      const hasCutsheet =
        !!buyer?.cutsheet || !!buyer?.questionnaireResponse?._id;

      if (!hasCutsheet) {
        allComplete = false;
        const isSelf = buyer.contactUser._id === selfUserId;

        if (isSelf) {
          // Producer needs to fill their own cutsheet → red
          return 'error';
        }

        // Non-self slot: check if reminder was sent (awaiting buyer action)
        const reminderSent = job.notificationHistory?.some(
          (n) =>
            n?.type === 'addCutsheets' &&
            n?.user?._id === buyer.contactUser?._id,
        );
        if (reminderSent) {
          hasWarning = true;
        } else {
          // No reminder sent yet, still needs producer action → red
          return 'error';
        }
      }
    }

    if (allComplete) return 'success';
    if (hasWarning) return 'warning';
    return 'error';
  }, [
    selectedSplit,
    slots,
    slotBuyerInfos,
    selfUserId,
    job.notificationHistory,
  ]);

  // Hide entire head for buyers when none of their splits are on it
  if (buyerMode && visibleSlotIndices.length === 0) return null;

  // Head is ready when every slot has a buyer and a cutsheet
  const isHeadReady =
    slots.length > 0 &&
    slots.every(
      (_s, i) =>
        !!slotBuyerInfos[i]?.contactUser &&
        (!!slotBuyerInfos[i]?.cutsheet ||
          !!slotBuyerInfos[i]?.questionnaireResponse?._id),
    );

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        bgcolor: 'background.paper',
        overflow: 'hidden',
        ...(isHeadReady && { borderColor: theme.palette.success.main }),
      }}
    >
      <CardContent
        sx={{
          p: 2,
          '&:last-child': { pb: 2 },
          ...(readOnly && { pointerEvents: 'none', opacity: 0.6 }),
        }}
      >
        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {producerId ? producerId : `${speciesLabel} #${index + 1}`}
          </Typography>
          {totalHangingWeight > 0 && (
            <Chip
              label={`${totalHangingWeight} lbs`}
              size="small"
              variant="outlined"
              color="primary"
            />
          )}
        </Stack>

        {/* Split type selector — hidden for buyers */}
        {!buyerMode && (
          <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
            {availableSplitTypes.map((opt) => {
              const isSelected = selectedSplit === opt;
              const helper = animalSplitTypeHelper(opt);
              return (
                <Button
                  key={opt}
                  variant="outlined"
                  size="small"
                  disabled={
                    isPendingAcceptance || savingSplitType || hasLockedSlot
                  }
                  onClick={() => requestSelectSplitType(opt)}
                  sx={{
                    textTransform: 'none',
                    flex: { xs: '1 1 calc(50% - 8px)', sm: 1 },
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    py: 1,
                    textAlign: 'left',
                    ...(isSelected
                      ? {
                          borderColor: `${splitHealth}.main`,
                          borderWidth: 2,
                          bgcolor: alpha(
                            theme.palette[splitHealth].main,
                            theme.palette.mode === 'dark' ? 0.2 : 0.1,
                          ),
                        }
                      : {}),
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    color={isSelected ? `${splitHealth}.main` : undefined}
                  >
                    {helper.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    color={isSelected ? `${splitHealth}.main` : 'inherit'}
                    sx={{ opacity: isSelected ? 0.8 : 0.7 }}
                  >
                    {getSplitDescription(opt)}
                  </Typography>
                </Button>
              );
            })}
          </Stack>
        )}

        {/* Split slots — two per row with divider between pairs */}
        {/* For buyers, only show slots where they are the assigned buyer */}
        {visibleSlotIndices.length > 0 && (
          <Stack gap={2}>
            {chunkSlots(visibleSlotIndices.map((i) => slots[i])).map(
              (row, ri) => (
                <Stack
                  key={ri}
                  sx={{
                    flexDirection: 'column',
                    '@media (min-width: 900px)': { flexDirection: 'row' },
                  }}
                  gap={2}
                  alignItems="stretch"
                >
                  {row.map((slot, ci) => {
                    const slotIdx = visibleSlotIndices[ri * 2 + ci];
                    const slotBuyer = slotBuyerInfos[slotIdx];
                    const slotHangingWeight = selectedSplit
                      ? calculateSplitHangingWeight(
                          {
                            hangingWeight: head?.hangingWeight ?? 0,
                            isHangingWeightPerSide:
                              head?.isHangingWeightPerSide ?? false,
                            hangingWeightSideA: head?.hangingWeightSideA ?? 0,
                            hangingWeightSideB: head?.hangingWeightSideB ?? 0,
                          },
                          selectedSplit,
                          slot.part,
                          slotIdx,
                        )
                      : 0;
                    // "Actual" = weight is exact (entered directly or summed from
                    // entered values): any whole, or per-side half mapped 1:1 to a side.
                    // Everything else is derived via a fraction → "calculated".
                    const isPerSide = head?.isHangingWeightPerSide ?? false;
                    const part = slot.part.toLowerCase();
                    let isHangingWeightCalculated = true;
                    if (part === 'whole') {
                      isHangingWeightCalculated = false;
                    } else if (part.startsWith('half') && isPerSide) {
                      isHangingWeightCalculated = false;
                    }
                    const responseId =
                      slotBuyer?.questionnaireResponse?._id ?? null;
                    const cutsheetName =
                      slotBuyer?.questionnaireResponse?.name ??
                      slotBuyer?.cutsheet?.name ??
                      undefined;
                    const hasResponse = !!responseId;
                    const openResponsePreview = hasResponse
                      ? () => setPreviewResponseId(responseId)
                      : undefined;

                    // Determine per-slot border color
                    const isSelf = slotBuyer?.contactUser?._id === selfUserId;
                    const hasCutsheet = !!slotBuyer?.cutsheet || hasResponse;
                    const reminderSent = job.notificationHistory?.some(
                      (n) =>
                        n?.type === 'addCutsheets' &&
                        n?.user?._id === slotBuyer?.contactUser?._id,
                    );
                    const isSlotLocked = slotBuyer?.locked === true;
                    let slotBorderColor: string | undefined;
                    if (isSlotLocked) {
                      slotBorderColor = theme.palette.warning.main;
                    } else if (
                      !slotBuyer?.contactUser ||
                      (!hasCutsheet && (isSelf || !reminderSent))
                    ) {
                      slotBorderColor = theme.palette.error.main;
                    } else if (!hasCutsheet && reminderSent) {
                      slotBorderColor = theme.palette.warning.main;
                    } else if (slotBuyer?.contactUser && hasCutsheet) {
                      slotBorderColor = theme.palette.success.main;
                    }

                    return (
                      <Fragment key={slot.label}>
                        <Card
                          variant="outlined"
                          sx={{
                            flex: '1 1 auto',
                            '@media (min-width: 900px)': {
                              flex: '1 1 0',
                              ...(row.length < 2 && {
                                maxWidth: 'calc(50% - 8px - 1px)',
                              }),
                            },
                            minWidth: 0,
                            borderRadius: 2,
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            ...(slotBorderColor && {
                              borderColor: slotBorderColor,
                            }),
                          }}
                        >
                          {buyerMode ? (
                            <BuyerSplitSlot
                              label={
                                visibleSlotIndices.length > 1
                                  ? slot.label
                                  : undefined
                              }
                              existingBuyer={slotBuyer ?? undefined}
                              cutsheetName={cutsheetName}
                              isQuestionnaireResponse={hasResponse}
                              onPreviewCutsheet={openResponsePreview}
                              isLocked={slotBuyer?.locked === true}
                              processorName={job.vendor?.shop_name ?? undefined}
                              downloadingPdf={generatingPdf}
                              onFillCutsheet={() => {
                                if (getSlotQuestionnaireId(slotIdx)) {
                                  setQuestionnaireResponseDrawerSlotIndex(
                                    slotIdx,
                                  );
                                } else {
                                  setCutsheetDrawerSlotIndex(slotIdx);
                                }
                              }}
                              onEditCutsheet={() => {
                                if (getSlotQuestionnaireId(slotIdx)) {
                                  if (hasResponse) {
                                    setQuestionnaireEditWizardSlotIndex(
                                      slotIdx,
                                    );
                                  } else {
                                    setQuestionnaireResponseDrawerSlotIndex(
                                      slotIdx,
                                    );
                                  }
                                  return;
                                }

                                const slotBuyerInfo = slotBuyerInfos[slotIdx];
                                const isSelf =
                                  slotBuyerInfo?.contactUser?._id === user?._id;
                                templateCutsheetRef.current =
                                  (slotBuyerInfo?.cutsheet as Cutsheet) ??
                                  undefined;
                                wizardContextRef.current = {
                                  cutsheetType: isSelf ? 'producer' : 'buyer',
                                  buyerId: !isSelf
                                    ? (slotBuyerInfo?.contactUser?._id ??
                                      undefined)
                                    : undefined,
                                  producerId: isSelf
                                    ? (user?._id ?? undefined)
                                    : undefined,
                                  userId:
                                    slotBuyerInfo?.contactUser?._id ??
                                    undefined,
                                };
                                setWizardSlotIndex(slotIdx);
                                fetchWizardData(job._id, user?._id ?? '');
                              }}
                              onDownloadPdf={() => handleDownloadPdf(slotIdx)}
                            />
                          ) : (
                            <SplitSlot
                              label={
                                visibleSlotIndices.length > 1
                                  ? slot.label
                                  : undefined
                              }
                              hangingWeight={slotHangingWeight}
                              isHangingWeightCalculated={
                                isHangingWeightCalculated
                              }
                              existingBuyer={slotBuyer ?? undefined}
                              isSelf={slotBuyer?.contactUser?._id === user?._id}
                              loading={savingSplitType}
                              disabled={isPendingAcceptance}
                              cutsheetName={cutsheetName}
                              isQuestionnaireResponse={hasResponse}
                              isLocked={slotBuyer?.locked === true}
                              processorName={job.vendor?.shop_name ?? undefined}
                              downloadingPdf={generatingPdf}
                              canSendReminder={slotCanSendReminder(slotBuyer)}
                              reminderSentTo={getReminderSentTo(slotBuyer)}
                              sendingReminder={sendingReminder}
                              myselfLabel={myselfLabel}
                              isProcessor={processorMode}
                              togglingLock={
                                togglingLockSlotId === slotBuyer?._id
                              }
                              onToggleLock={() => handleToggleSlotLock(slotIdx)}
                              onSendReminder={() => handleSendReminder(slotIdx)}
                              onSelectMyself={() => handleSelectMyself(slotIdx)}
                              onSelectExisting={() =>
                                setDrawerSlotIndex(slotIdx)
                              }
                              onSelectNew={() => setNewBuyerSlotIndex(slotIdx)}
                              onChangeBuyer={() => setDrawerSlotIndex(slotIdx)}
                              onLeaveUnassigned={() =>
                                handleLeaveUnassigned(slotIdx)
                              }
                              onFillCutsheet={() => {
                                if (getSlotQuestionnaireId(slotIdx)) {
                                  setQuestionnaireResponseDrawerSlotIndex(
                                    slotIdx,
                                  );
                                } else {
                                  setCutsheetDrawerSlotIndex(slotIdx);
                                }
                              }}
                              onEditCutsheet={() => {
                                if (getSlotQuestionnaireId(slotIdx)) {
                                  if (hasResponse) {
                                    setQuestionnaireEditWizardSlotIndex(
                                      slotIdx,
                                    );
                                  } else {
                                    setQuestionnaireResponseDrawerSlotIndex(
                                      slotIdx,
                                    );
                                  }
                                  return;
                                }

                                const slotBuyerInfo = slotBuyerInfos[slotIdx];
                                const isSelf =
                                  slotBuyerInfo?.contactUser?._id === user?._id;
                                templateCutsheetRef.current =
                                  (slotBuyerInfo?.cutsheet as Cutsheet) ??
                                  undefined;
                                wizardContextRef.current = {
                                  cutsheetType: isSelf ? 'producer' : 'buyer',
                                  buyerId: !isSelf
                                    ? (slotBuyerInfo?.contactUser?._id ??
                                      undefined)
                                    : undefined,
                                  producerId: isSelf
                                    ? (user?._id ?? undefined)
                                    : undefined,
                                  userId:
                                    slotBuyerInfo?.contactUser?._id ??
                                    undefined,
                                };
                                setWizardSlotIndex(slotIdx);
                                fetchWizardData(job._id, user?._id ?? '');
                              }}
                              onDownloadPdf={() => handleDownloadPdf(slotIdx)}
                              questionnaireOptions={
                                hasCapabilitiesV2
                                  ? questionnaireOptions
                                  : undefined
                              }
                              currentQuestionnaireId={getSlotQuestionnaireId(
                                slotIdx,
                              )}
                              onSelectQuestionnaire={(id) =>
                                handleSelectQuestionnaire(slotIdx, id)
                              }
                              onPreviewCutsheet={openResponsePreview}
                              onAddYield={
                                processorMode && slotBuyer?._id
                                  ? () => setEditYieldSlotIndex(slotIdx)
                                  : undefined
                              }
                              yieldCount={slotBuyer?.yield?.length ?? 0}
                              onGeneratePackingSlip={
                                slotBuyer?._id
                                  ? () => handleGeneratePackingSlip(slotIdx)
                                  : undefined
                              }
                              generatingPackingSlip={
                                packingSlipSlotIndex === slotIdx
                              }
                            />
                          )}
                        </Card>
                      </Fragment>
                    );
                  })}
                </Stack>
              ),
            )}
          </Stack>
        )}
      </CardContent>

      <ExistingBuyerDrawer
        open={drawerSlotIndex != null}
        vendorId={job.vendor?._id}
        producerId={processorMode ? undefined : job.requestedBy?._id}
        relationshipType={
          processorMode ? undefined : EnumUserRelationshipsType.Buyer
        }
        title={
          processorMode ? 'Select Existing Customer' : 'Select Existing Buyer'
        }
        myselfLabel={myselfLabel}
        onSelect={handleSelectExisting}
        onSelectMyself={() => {
          if (drawerSlotIndex != null) handleSelectMyself(drawerSlotIndex);
        }}
        onOpenNewBuyer={() => {
          setNewBuyerSlotIndex(drawerSlotIndex);
        }}
        onClose={() => setDrawerSlotIndex(null)}
      />

      <NewBuyerDrawer
        open={newBuyerSlotIndex != null}
        vendorId={vendor?._id ?? job.vendor?._id}
        producerId={user?._id ?? ''}
        onCreated={handleNewBuyerCreated}
        onClose={() => setNewBuyerSlotIndex(null)}
      />

      {cutsheetDrawerInfo && head && (
        <ProducerCutsheetDrawer
          open={cutsheetDrawerSlotIndex != null}
          onClose={() => setCutsheetDrawerSlotIndex(null)}
          onCreateNew={() => {
            const drawerBuyer =
              cutsheetDrawerSlotIndex != null
                ? slotBuyerInfos[cutsheetDrawerSlotIndex]
                : null;
            const isSelf = drawerBuyer?.contactUser?._id === user?._id;
            wizardContextRef.current = {
              cutsheetType: isSelf ? 'producer' : 'buyer',
              buyerId: !isSelf
                ? (drawerBuyer?.contactUser?._id ?? undefined)
                : undefined,
              producerId: isSelf ? (user?._id ?? undefined) : undefined,
              userId: drawerBuyer?.contactUser?._id ?? undefined,
            };
            setCutsheetDrawerSlotIndex(null);
            setWizardSlotIndex(cutsheetDrawerSlotIndex);
            fetchWizardData(job._id, user?._id ?? '');
          }}
          job={job}
          head={head}
          cutsheetInfo={cutsheetDrawerInfo}
        />
      )}

      {previewResponseId && (vendor?._id ?? job.vendor?._id) && (
        <CutsheetResponsePreview
          onClose={() => setPreviewResponseId(null)}
          responseId={previewResponseId}
          vendorId={(vendor?._id ?? job.vendor?._id) as string}
        />
      )}

      {questionnaireResponseDrawerInfo && (
        <ProducerQuestionnaireResponseDrawer
          open={questionnaireResponseDrawerSlotIndex != null}
          onClose={() => setQuestionnaireResponseDrawerSlotIndex(null)}
          job={job}
          cutsheetInfo={questionnaireResponseDrawerInfo}
        />
      )}

      {questionnaireEditWizardInfo && (
        <ProducerQuestionnaireEditWizard
          open={questionnaireEditWizardSlotIndex != null}
          onClose={() => setQuestionnaireEditWizardSlotIndex(null)}
          job={job}
          cutsheetInfo={questionnaireEditWizardInfo}
        />
      )}

      {wizardSlotIndex != null && (
        <CutsheetWizardForm
          open={wizardSlotIndex != null && wizardDataReady}
          onClose={() => {
            setWizardSlotIndex(null);
            templateCutsheetRef.current = undefined;
          }}
          templateCutsheet={templateCutsheetRef.current}
          capabilities={wizardCapabilities}
          settings={wizardSettings}
          animalSpecies={
            job.animalSpecies as unknown as EnumCutsheetAnimalSpecies
          }
          inspectionLevel={
            head?.inspectionLevel as unknown as EnumCutsheetInspectionLevels
          }
          cutsheetType={undefined}
          buyerId={wizardContextRef.current?.buyerId}
          producerId={wizardContextRef.current?.producerId}
          userId={wizardContextRef.current?.userId}
          onSubmitWithCutsheet={(cutsheet) => {
            handleWizardCutsheetCreated(cutsheet);
          }}
        />
      )}
      {pendingSplitType && (
        <ConfirmDestructiveDialog
          title="Change split type?"
          body="Changing the split type will clear all buyers, cutsheets, and saved responses on this head. This cannot be undone."
          confirmLabel="Change split type"
          onCancel={() => setPendingSplitType(null)}
          onConfirm={() => {
            const next = pendingSplitType;
            setPendingSplitType(null);
            handleSelectSplitType(next);
          }}
        />
      )}
      {editYieldSlotIndex != null &&
        (() => {
          const splitForModal = buildEditYieldSplit(editYieldSlotIndex);
          if (!splitForModal) return null;
          return (
            <EditYieldModal
              open
              onClose={() => setEditYieldSlotIndex(null)}
              split={splitForModal}
              processorSchedulingId={job._id}
            />
          );
        })()}
    </Card>
  );
}
